import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { BRAIN_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from '@/components/ui/item';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Brain } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface DeleteAgentFieldsetProps {
  brain: Brain;
  brainId: string;
}

export const DeleteAgentFieldset: React.FC<DeleteAgentFieldsetProps> = ({ brain, brainId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const handleDelete = useCallback(() => {
    const warnProps = {
      brainName: brain?.name,
      brainId: brainId,
      onDeleted: () => navigate(`/${slug}/ai-agents`, { replace: true }),
    };
    dispatch(pushModal(BRAIN_DELETE, warnProps));
  }, [dispatch, brain?.name, brainId, navigate, slug]);

  return (
    <Item variant="outline" className="rounded-lg p-6 pb-7">
      <ItemContent>
        <ItemTitle>{t('ai_agents.delete.title')}</ItemTitle>
        <ItemDescription>{t('ai_agents.delete.subtitle')}</ItemDescription>
      </ItemContent>
      <ItemActions>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          onClick={handleDelete}
          disabled={!canWrite}
        >
          {t('common.delete')}
        </Button>
      </ItemActions>
    </Item>
  );
};
